import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { MdArrowBack, MdArrowForward, MdShuffle, MdQuiz, MdLock } from 'react-icons/md';
import axios from 'config/axios';
import { useAppSelector } from 'hooks/useStore';
import useStudySession from 'hooks/useStudySession';
import Loader from 'components/Loader';

import './index.css';

// Status constants — maps to Django's StudentVocabProgress model.
const STATUS = { NEW: 1, LEARNING: 2, MASTERED: 3 } as const;

type DeckView = 'study' | 'learning' | 'mastered';

function Flashcards() {
    const [words, setWords] = useState(null);
    const [vocabSetName, setVocabSetName] = useState('');
    const [studentVocabSetId, setStudentVocabSetId] = useState<number | null>(null);
    const [flipped, setFlipped] = useState(false);
    const [pendingUpdates, setPendingUpdates] = useState([]);

    // Which deck the student is currently viewing
    const [activeDeck, setActiveDeck] = useState<DeckView>('study');
    const [deckIndex, setDeckIndex] = useState(0);

    // Card exit animation: 'left' (learning), 'right' (mastered), or null
    const [exitDirection, setExitDirection] = useState<'left' | 'right' | null>(null);
    const [shuffling, setShuffling] = useState(false);

    // Mastery completion popup
    const [showMasteryPopup, setShowMasteryPopup] = useState(false);
    const [dismissedMastery, setDismissedMastery] = useState(false);

    // Quiz access
    const [quizData, setQuizData] = useState(null);

    // Refs for stale-closure fixes — keyboard handler and unmount flush
    // always read from refs to get the latest values.
    const pendingUpdatesRef = useRef([]);
    const studentVocabSetIdRef = useRef<number | null>(null);
    const wordsRef = useRef(null);
    const activeDeckRef = useRef<DeckView>('study');
    const deckIndexRef = useRef(0);
    const exitDirectionRef = useRef<'left' | 'right' | null>(null);
    const dismissedMasteryRef = useRef(false);

    useEffect(() => { pendingUpdatesRef.current = pendingUpdates; }, [pendingUpdates]);
    useEffect(() => { studentVocabSetIdRef.current = studentVocabSetId; }, [studentVocabSetId]);
    useEffect(() => { wordsRef.current = words; }, [words]);
    useEffect(() => { activeDeckRef.current = activeDeck; }, [activeDeck]);
    useEffect(() => { deckIndexRef.current = deckIndex; }, [deckIndex]);
    useEffect(() => { exitDirectionRef.current = exitDirection; }, [exitDirection]);
    useEffect(() => { dismissedMasteryRef.current = dismissedMastery; }, [dismissedMastery]);

    const { vocabSetId } = useParams();
    const navigate = useNavigate();
    const { organization } = useAppSelector(state => state.organization);

    useStudySession({
        activityType: 'vocab_flashcards',
        activityIds: { studentVocabSetId: studentVocabSetId ?? undefined },
        enabled: studentVocabSetId !== null,
    });

    // --- Derived decks from words array ---
    const studyCards = words ? words.filter(w => w.progress.status === STATUS.NEW) : [];
    const learningCards = words ? words.filter(w => w.progress.status === STATUS.LEARNING) : [];
    const masteredCards = words ? words.filter(w => w.progress.status === STATUS.MASTERED) : [];

    const currentDeck = activeDeck === 'study' ? studyCards
        : activeDeck === 'learning' ? learningCards
        : masteredCards;

    const currentCard = currentDeck[deckIndex] || null;

    // --- Data fetching ---
    useEffect(() => {
        axios.get(`vocab/words/${vocabSetId}/`)
            .then(res => {
                setWords(res.data.words);
                setVocabSetName(res.data.name);
                setStudentVocabSetId(res.data.studentVocabSetId);
                if (res.data.completedDate) {
                    setDismissedMastery(true);
                }

                // Fetch quiz access for this set
                axios.get(`vocab/quiz/sets/${organization.urlSlug}/`)
                    .then(quizRes => {
                        const match = quizRes.data.sets.find(s => s.studentVocabSetId === res.data.studentVocabSetId);
                        if (match) setQuizData(match);
                    })
                    .catch(() => {});
            })
            .catch(error => {
                console.error(error);
                navigate('/vocab');
            });
    }, [vocabSetId, navigate]);

    // --- Batch sync ---
    useEffect(() => {
        if (pendingUpdates.length >= 5) {
            flushUpdates(pendingUpdates);
            setPendingUpdates([]);
        }
    }, [pendingUpdates]);

    // Flush remaining updates on unmount only. The back button just navigates
    // (triggering unmount), avoiding a double-flush.
    useEffect(() => {
        return () => {
            if (pendingUpdatesRef.current.length > 0) {
                flushUpdates(pendingUpdatesRef.current);
            }
        };
    }, []);

    const flushUpdates = (updates) => {
        if (updates.length === 0) return;
        axios.post('vocab/progress/batch/', {
            updates: updates,
            studentVocabSetId: studentVocabSetIdRef.current,
        }).catch(error => console.error(error));
    };

    // --- Card sorting: mark a word and animate it to the target pile ---
    const markWord = (status: number) => {
        if (!currentCard || exitDirection) return;

        // Trigger exit animation
        setExitDirection(status === STATUS.LEARNING ? 'left' : 'right');

        // After animation, update state
        setTimeout(() => {
            const newWords = words.map(w =>
                w.id === currentCard.id
                    ? {
                        ...w,
                        progress: {
                            ...w.progress,
                            status: status,
                            statusDisplay: status === STATUS.MASTERED ? 'Mastered' : 'Learning',
                            timesReviewed: w.progress.timesReviewed + 1,
                        }
                    }
                    : w
            );
            setWords(newWords);

            const thisUpdate = { vocabWordId: currentCard.id, status: status };

            // Reset animation and clamp index
            setExitDirection(null);
            setFlipped(false);

            // After removing a card from current deck, clamp index.
            // The deck will be one shorter after this state update.
            const deckAfter = activeDeck === 'study'
                ? newWords.filter(w => w.progress.status === STATUS.NEW)
                : activeDeck === 'learning'
                ? newWords.filter(w => w.progress.status === STATUS.LEARNING)
                : newWords.filter(w => w.progress.status === STATUS.MASTERED);

            if (deckIndex >= deckAfter.length) {
                setDeckIndex(Math.max(0, deckAfter.length - 1));
            }

            // Check if all words are now mastered — flush immediately so
            // the backend sets completedDate and tutors see it right away.
            const allMastered = newWords.every(w => w.progress.status === STATUS.MASTERED);
            if (allMastered) {
                flushUpdates([...pendingUpdatesRef.current, thisUpdate]);
                pendingUpdatesRef.current = [];  // Reset ref synchronously to prevent double-flush
                setPendingUpdates([]);

                if (!dismissedMasteryRef.current) {
                    setShowMasteryPopup(true);
                }
            } else {
                // Normal queue for server sync
                setPendingUpdates(prev => [...prev, thisUpdate]);
            }
        }, 300);
    };

    // --- Move card from mastered back to learning ---
    const moveToLearning = () => {
        if (!currentCard || activeDeck !== 'mastered') return;

        setExitDirection('left');

        setTimeout(() => {
            const newWords = words.map(w =>
                w.id === currentCard.id
                    ? {
                        ...w,
                        progress: {
                            ...w.progress,
                            status: STATUS.LEARNING,
                            statusDisplay: 'Learning',
                        }
                    }
                    : w
            );
            setWords(newWords);

            setPendingUpdates(prev => [...prev, {
                vocabWordId: currentCard.id,
                status: STATUS.LEARNING,
            }]);

            setExitDirection(null);
            setFlipped(false);
            const deckAfter = newWords.filter(w => w.progress.status === STATUS.MASTERED);
            if (deckIndex >= deckAfter.length) {
                setDeckIndex(Math.max(0, deckAfter.length - 1));
            }
        }, 300);
    };

    // --- Navigation within a deck ---
    const goNext = () => {
        if (exitDirection || currentDeck.length <= 1) return;
        setFlipped(false);
        setDeckIndex(prev => prev < currentDeck.length - 1 ? prev + 1 : 0);
    };

    const goPrev = () => {
        if (exitDirection || deckIndex <= 0) return;
        setFlipped(false);
        setDeckIndex(prev => prev - 1);
    };

    // --- Switch between decks ---
    const switchDeck = (deck: DeckView) => {
        setActiveDeck(deck);
        setDeckIndex(0);
        setFlipped(false);
        setExitDirection(null);
    };

    // --- Shuffle current deck (Fisher-Yates on the words array) ---
    const shuffleDeck = () => {
        if (!words || currentDeck.length <= 1 || shuffling) return;

        // Start animation
        setShuffling(true);
        setFlipped(false);

        // After the animation plays, do the actual shuffle
        setTimeout(() => {
            const targetStatus = activeDeck === 'study' ? STATUS.NEW
                : activeDeck === 'learning' ? STATUS.LEARNING
                : STATUS.MASTERED;

            const deckCards = [...words.filter(w => w.progress.status === targetStatus)];
            const otherCards = words.filter(w => w.progress.status !== targetStatus);

            // Fisher-Yates shuffle
            for (let i = deckCards.length - 1; i > 0; i--) {
                const j = Math.floor(Math.random() * (i + 1));
                [deckCards[i], deckCards[j]] = [deckCards[j], deckCards[i]];
            }

            // Rebuild words array preserving positions of other decks
            const shuffled = [];
            let deckIdx = 0;
            let otherIdx = 0;
            for (const w of words) {
                if (w.progress.status === targetStatus) {
                    shuffled.push(deckCards[deckIdx++]);
                } else {
                    shuffled.push(otherCards[otherIdx++]);
                }
            }

            setWords(shuffled);
            setDeckIndex(0);
            setShuffling(false);
        }, 400);
    };

    // --- Mastery popup handlers ---
    const handleCompleteSet = () => {
        // Flush any remaining updates immediately, then navigate back
        const remaining = pendingUpdatesRef.current;
        if (remaining.length > 0) {
            flushUpdates(remaining);
            setPendingUpdates([]);
        }
        setShowMasteryPopup(false);
        navigate('/vocab');
    };

    const handleContinueStudying = () => {
        setShowMasteryPopup(false);
        setDismissedMastery(true);
    };

    // --- Keyboard shortcuts ---
    // Uses refs so the handler always sees the latest state without
    // needing to recreate the effect on every render.
    // markWord and moveToLearning are stored in refs to avoid stale closures.
    const markWordRef = useRef(markWord);
    const moveToLearningRef = useRef(moveToLearning);
    useEffect(() => { markWordRef.current = markWord; }, [words, currentCard, exitDirection, activeDeck, deckIndex, pendingUpdates, dismissedMastery]);
    useEffect(() => { moveToLearningRef.current = moveToLearning; }, [words, currentCard, activeDeck, deckIndex, pendingUpdates]);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === ' ' || e.key === 'Enter') {
                // Only flip when no button is focused, to avoid hijacking button activation
                if (document.activeElement?.tagName !== 'BUTTON') {
                    e.preventDefault();
                    setFlipped(prev => !prev);
                }
            } else if (e.key === 'ArrowRight') {
                e.preventDefault();
                if (!exitDirectionRef.current) {
                    setFlipped(false);
                    setDeckIndex(prev => {
                        const w = wordsRef.current;
                        if (!w) return prev;
                        const deck = activeDeckRef.current;
                        const status = deck === 'study' ? STATUS.NEW : deck === 'learning' ? STATUS.LEARNING : STATUS.MASTERED;
                        const deckLen = w.filter(word => word.progress.status === status).length;
                        if (deckLen <= 1) return prev;
                        return prev < deckLen - 1 ? prev + 1 : 0;
                    });
                }
            } else if (e.key === 'ArrowLeft') {
                e.preventDefault();
                if (!exitDirectionRef.current) {
                    setFlipped(false);
                    setDeckIndex(prev => prev > 0 ? prev - 1 : prev);
                }
            } else if (e.key === '1') {
                if (activeDeckRef.current === 'study') markWordRef.current(STATUS.LEARNING);
                else if (activeDeckRef.current === 'mastered') moveToLearningRef.current();
            } else if (e.key === '2') {
                if (activeDeckRef.current === 'study' || activeDeckRef.current === 'learning') markWordRef.current(STATUS.MASTERED);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    if (words === null) return <Loader />;

    // If quiz is in progress, lock flashcards
    if (quizData?.inProgressAttempt) {
        return (
            <>
                <Helmet>
                    <title>{vocabSetName} - {organization.name}</title>
                </Helmet>
                <div className='fc'>
                    <div className='fc__topBar'>
                        <button className='fc__back' onClick={() => navigate('/vocab')}>
                            <MdArrowBack /> Back to Vocab
                        </button>
                        <h2 className='fc__title'>{vocabSetName}</h2>
                    </div>
                    <div className='fc__locked'>
                        <MdLock className='fc__lockedIcon' />
                        <h2 className='fc__lockedTitle'>Flashcards Locked</h2>
                        <p className='fc__lockedText'>
                            You have a quiz in progress for this set. Flashcards are hidden while a quiz is active to keep your results fair. Resume or discard your quiz to access flashcards again.
                        </p>
                        <button
                            className='fc__quizBtn'
                            onClick={() => navigate(`/vocab/quiz/${vocabSetId}`, {
                                state: { resumeQuizId: quizData.inProgressAttempt.id },
                            })}
                        >
                            <MdQuiz /> Resume Quiz {quizData.inProgressAttempt.testNumber}
                        </button>
                    </div>
                </div>
            </>
        );
    }

    const totalWords = words.length;
    const safePct = (n: number) => totalWords > 0 ? (n / totalWords) * 100 : 0;

    // --- Empty study deck view ---
    const renderEmptyStudy = () => (
        <div className='fc__empty'>
            <div className='fc__emptyIcon'>&#10003;</div>
            <h2 className='fc__emptyTitle'>All cards sorted!</h2>
            <p className='fc__emptyText'>
                You've gone through all {totalWords} words.
                {learningCards.length > 0 && ` You have ${learningCards.length} cards still to master.`}
            </p>
            {learningCards.length > 0 && (
                <button className='fc__emptyButton fc__emptyButton--learning' onClick={() => switchDeck('learning')}>
                    Review Still Learning ({learningCards.length})
                </button>
            )}
            {masteredCards.length === totalWords && (
                <p className='fc__emptyText' style={{ marginBottom: 0 }}>
                    You've mastered all {totalWords} words!
                </p>
            )}
        </div>
    );

    // --- Empty side deck view ---
    const renderEmptyDeck = () => (
        <div className='fc__empty'>
            <p className='fc__emptyText'>No cards in this pile yet.</p>
            {studyCards.length > 0 && (
                <button className='fc__emptyButton fc__emptyButton--reset' onClick={() => switchDeck('study')}>
                    Back to Study Deck
                </button>
            )}
        </div>
    );

    // --- Card face rendering ---
    const renderCardContent = (word) => (
        <>
            <div className='fc__cardFront'>
                <h1 className='fc__word'>{word.word}</h1>
                <p className='fc__partOfSpeech'>{word.partOfSpeech}</p>
                <p className='fc__hint'>Click to flip</p>
            </div>
            <div className='fc__cardBack'>
                <h2 className='fc__definition'>{word.definition}</h2>
                {word.connotation && (
                    <p className='fc__connotation'>
                        Connotation: <span className={`fc__connotation--${word.connotation}`}>{word.connotation}</span>
                    </p>
                )}
                {word.exampleSentence && (
                    <p className='fc__example'>"{word.exampleSentence}"</p>
                )}
            </div>
        </>
    );

    return (
        <>
            <Helmet>
                <title>{vocabSetName} - {organization.name}</title>
            </Helmet>
            <div className='fc'>
                {/* Top bar */}
                <div className='fc__topBar'>
                    <button className='fc__back' onClick={() => navigate('/vocab')}>
                        <MdArrowBack /> Back to Vocab
                    </button>
                    <h2 className='fc__title'>{vocabSetName}</h2>
                    {quizData && (
                        <button
                            className='fc__quizBtn'
                            onClick={() => {
                                // Flush pending updates before navigating
                                if (pendingUpdatesRef.current.length > 0) {
                                    flushUpdates(pendingUpdatesRef.current);
                                    setPendingUpdates([]);
                                }
                                if (quizData.inProgressAttempt) {
                                    navigate(`/vocab/quiz/${vocabSetId}`, {
                                        state: { resumeQuizId: quizData.inProgressAttempt.id },
                                    });
                                } else {
                                    navigate(`/vocab/quiz/${vocabSetId}`, {
                                        state: { studentVocabSetId },
                                    });
                                }
                            }}
                        >
                            <MdQuiz />
                            {quizData.inProgressAttempt
                                ? `Resume Quiz ${quizData.inProgressAttempt.testNumber}`
                                : `Quiz ${(quizData.completedAttempts.length % 4) + 1}`
                            }
                        </button>
                    )}
                </div>

                {/* Progress bar */}
                <div className='fc__progressWrap'>
                    <div className='fc__progressBar'>
                        <span className='fc__progressFill--mastered' style={{ width: `${safePct(masteredCards.length)}%` }}></span>
                        <span className='fc__progressFill--learning' style={{ width: `${safePct(learningCards.length)}%` }}></span>
                    </div>
                    <p className='fc__progressLabel'>
                        {masteredCards.length} mastered · {learningCards.length} learning · {studyCards.length} remaining
                    </p>
                </div>

                {/* Three-column layout */}
                <div className='fc__columns'>

                    {/* Left pile: Learning */}
                    <div
                        className={`fc__pile fc__pile--learning ${activeDeck === 'learning' ? 'fc__pile--active' : ''}`}
                        onClick={() => learningCards.length > 0 && switchDeck('learning')}
                    >
                        <div className='fc__pileHeader'>
                            <span className='fc__pileDot fc__pileDot--learning'></span>
                            <span className='fc__pileLabel'>Still Learning</span>
                        </div>
                        <div className='fc__pileStack'>
                            {learningCards.length > 0 ? (
                                <>
                                    {learningCards.length > 2 && <div className='fc__pileCard fc__pileCard--3 fc__pileCard--learning'></div>}
                                    {learningCards.length > 1 && <div className='fc__pileCard fc__pileCard--2 fc__pileCard--learning'></div>}
                                    <div className='fc__pileCard fc__pileCard--1 fc__pileCard--learning'>
                                        <span className='fc__pileWord'>{learningCards[0].word}</span>
                                    </div>
                                </>
                            ) : (
                                <div className='fc__pileEmpty'>Empty</div>
                            )}
                        </div>
                        <span className='fc__pileCount'>{learningCards.length} cards</span>
                    </div>

                    {/* Center: Active study area */}
                    <div className='fc__center'>
                        {activeDeck !== 'study' && (
                            <button className='fc__deckTab' onClick={() => switchDeck('study')}>
                                <MdArrowBack /> Back to Study Deck ({studyCards.length})
                            </button>
                        )}

                        {activeDeck !== 'study' && (
                            <p className='fc__deckLabel'>
                                Reviewing: {activeDeck === 'learning' ? 'Still Learning' : 'Mastered'} ({currentDeck.length} cards)
                            </p>
                        )}

                        {currentDeck.length === 0 ? (
                            activeDeck === 'study' ? renderEmptyStudy() : renderEmptyDeck()
                        ) : (
                            <>
                                <div className='fc__cardContainer' onClick={() => !exitDirection && setFlipped(!flipped)}>
                                    <div className={`fc__card ${flipped ? 'fc__card--flipped' : ''} ${exitDirection === 'left' ? 'fc__card--exitLeft' : ''} ${exitDirection === 'right' ? 'fc__card--exitRight' : ''} ${shuffling ? 'fc__card--shuffling' : ''}`}>
                                        {currentCard && renderCardContent(currentCard)}
                                    </div>
                                </div>

                                <div className='fc__counterRow'>
                                    <p className='fc__counter'>
                                        {deckIndex + 1} of {currentDeck.length}
                                    </p>
                                    {currentDeck.length > 1 && (
                                        <button className='fc__shuffleBtn' onClick={shuffleDeck}>
                                            <MdShuffle /> Shuffle
                                        </button>
                                    )}
                                </div>

                                {/* Action buttons */}
                                <div className='fc__actions'>
                                    <button className='fc__nav' onClick={goPrev} disabled={deckIndex === 0 || !!exitDirection}>
                                        <MdArrowBack />
                                    </button>

                                    {/* Study + Learning decks: sort into Still Learning or Mastered */}
                                    {(activeDeck === 'study' || activeDeck === 'learning') && (
                                        <>
                                            {activeDeck === 'study' && (
                                                <button className='fc__button fc__button--dontKnow' onClick={() => markWord(STATUS.LEARNING)} disabled={!!exitDirection}>
                                                    Still Learning
                                                </button>
                                            )}
                                            <button className='fc__button fc__button--knowIt' onClick={() => markWord(STATUS.MASTERED)} disabled={!!exitDirection}>
                                                Mastered
                                            </button>
                                        </>
                                    )}

                                    {/* Mastered deck: can only demote back to learning */}
                                    {activeDeck === 'mastered' && (
                                        <button className='fc__button fc__button--dontKnow' onClick={moveToLearning} disabled={!!exitDirection}>
                                            Move to Still Learning
                                        </button>
                                    )}

                                    <button className='fc__nav' onClick={goNext} disabled={currentDeck.length <= 1 || !!exitDirection}>
                                        <MdArrowForward />
                                    </button>
                                </div>

                                <p className='fc__shortcuts'>
                                    Space: flip &nbsp; Arrows: navigate
                                    {activeDeck === 'study' && <>&nbsp; 1: Still Learning &nbsp; 2: Mastered</>}
                                    {activeDeck === 'learning' && <>&nbsp; 2: Mastered</>}
                                    {activeDeck === 'mastered' && <>&nbsp; 1: Move to Learning</>}
                                </p>
                            </>
                        )}
                    </div>

                    {/* Right pile: Mastered */}
                    <div
                        className={`fc__pile fc__pile--mastered ${activeDeck === 'mastered' ? 'fc__pile--active' : ''}`}
                        onClick={() => masteredCards.length > 0 && switchDeck('mastered')}
                    >
                        <div className='fc__pileHeader'>
                            <span className='fc__pileDot fc__pileDot--mastered'></span>
                            <span className='fc__pileLabel'>Mastered</span>
                        </div>
                        <div className='fc__pileStack'>
                            {masteredCards.length > 0 ? (
                                <>
                                    {masteredCards.length > 2 && <div className='fc__pileCard fc__pileCard--3 fc__pileCard--mastered'></div>}
                                    {masteredCards.length > 1 && <div className='fc__pileCard fc__pileCard--2 fc__pileCard--mastered'></div>}
                                    <div className='fc__pileCard fc__pileCard--1 fc__pileCard--mastered'>
                                        <span className='fc__pileWord'>{masteredCards[0].word}</span>
                                    </div>
                                </>
                            ) : (
                                <div className='fc__pileEmpty'>Empty</div>
                            )}
                        </div>
                        <span className='fc__pileCount'>{masteredCards.length} cards</span>
                    </div>

                </div>

                {/* Persistent "Mark as Complete" button after dismissing popup */}
                {dismissedMastery && masteredCards.length === totalWords && (
                    <button className='fc__completeBtn' onClick={handleCompleteSet}>
                        Mark as Complete
                    </button>
                )}
            </div>

            {/* Mastery congratulations popup */}
            {showMasteryPopup && (
                <div className='fc__overlay'>
                    <div className='fc__popup'>
                        <div className='fc__popupIcon'>&#127881;</div>
                        <h2 className='fc__popupTitle'>You mastered the set!</h2>
                        <p className='fc__popupText'>
                            You've mastered all {totalWords} words in {vocabSetName}.
                        </p>
                        <div className='fc__popupActions'>
                            <button className='fc__popupBtn fc__popupBtn--complete' onClick={handleCompleteSet}>
                                Complete Set
                            </button>
                            <button className='fc__popupBtn fc__popupBtn--continue' onClick={handleContinueStudying}>
                                Continue Studying
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default Flashcards;
